import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";

const SuperAdminAddOrderDetails = () => {
  const [shippingCharge, setShippingCharge] = useState("");
  const [taxCharge, setTaxCharge] = useState("");
  const [orderDetails, setOrderDetails] = useState([]);
  const [isDataPosted, setIsDataPosted] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, []);

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(
        "https://servercareseebd.careseebd.com/api/v1/orderDetails/getOrderDetails"
      );
      const responseData = await response.json();

      if (response.ok) {
        if (Array.isArray(responseData.data)) {
          setOrderDetails(responseData.data);
          setIsDataPosted(responseData.data.length > 0);
        } else {
          console.error("API response data is not an array:", responseData);
        }
      } else {
        console.error("Error fetching order details:", responseData);
      }
    } catch (error) {
      console.error("API request failed:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDataPosted) {
      console.log("Data has already been posted. Delete it first.");
      return;
    }

    try {
      const response = await fetch(
        "https://servercareseebd.careseebd.com/api/v1/orderDetails/addOrderDetails",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            shippingCost: parseFloat(shippingCharge),
            taxRate: parseFloat(taxCharge),
          }),
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        setShippingCharge("");
        setTaxCharge("");
        e.target.reset();
        setIsDataPosted(true);
        fetchOrderDetails();
      } else {
        console.error("Error creating OrderDetails:", responseData);
      }
    } catch (error) {
      console.error("API request failed:", error);
    }
  };

  const handleDelete = async (orderId) => {
    try {
      const response = await fetch(
        `https://servercareseebd.careseebd.com/api/v1/orderDetails/deleteOrderDetails/${orderId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        setIsDataPosted(false);
        fetchOrderDetails();
      } else {
        console.error("Error deleting OrderDetails");
      }
    } catch (error) {
      console.error("API request failed:", error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto select-none space-y-6">
      {/* Header card */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:bill-list-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Shipping & Taxes</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Configure platform delivery costs and tax percentages</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Form panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Configure Rates</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Shipping Cost (BDT)</label>
              <input
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
                placeholder="Enter charge"
                value={shippingCharge}
                onChange={(e) => setShippingCharge(e.target.value)}
                disabled={isDataPosted}
                required
              />
            </div>

            <div className="space-y-1">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Tax Rate (%)</label>
              <input
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
                placeholder="Enter rate"
                value={taxCharge}
                onChange={(e) => setTaxCharge(e.target.value)}
                disabled={isDataPosted}
                required
              />
            </div>

            <button
              type="submit"
              className={`w-full py-2.5 rounded-xl font-extrabold text-xs text-white transition-all shadow-md cursor-pointer ${isDataPosted
                ? "bg-slate-300 shadow-none cursor-not-allowed"
                : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 shadow-cyan-500/10 hover:-translate-y-0.5"
                }`}
              disabled={isDataPosted}
            >
              {isDataPosted ? "Rates Already Set" : "Save Settings"}
            </button>
          </form>
        </div>

        {/* Display panel */}
        <div className="md:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Active Configuration</h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Shipping Cost</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Tax Rate</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orderDetails.length > 0 ? (
                  orderDetails.map((orderDetail) => (
                    <tr key={orderDetail._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-bold text-slate-800">
                        {orderDetail.shippingCost} BDT
                      </td>
                      <td className="px-6 py-4 text-xs font-bold text-indigo-600">
                        {orderDetail.taxRate}%
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleDelete(orderDetail._id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-150 rounded-lg transition-all cursor-pointer"
                            title="Delete Configuration"
                          >
                            <Icon icon="material-symbols:delete-outline" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-10 text-xs text-slate-400">
                      No rates configured. Please submit new rates using the left panel.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminAddOrderDetails;
